/**
 * Job Detail Modal - Read-Only View
 *
 * Shows comprehensive job information when clicked from customer view or dashboards
 */

import { getLocaleSync } from '@/utils/locale';
import { useState } from 'react';
import { X, MapPin, Calendar, Clock, User, Briefcase, FileText, AlertCircle, Wifi } from 'lucide-react';
import { Job } from '../types/schema';
import { ActivateServiceModal } from './ActivateServiceModal';
import { useFeatureGate } from '@/hooks/useFeatureGate';
import { PhoneNumber } from './PhoneNumber';
import { CustomerLink } from './customers/CustomerLink';
import { SiteAddressNote } from './jobs/SiteAddressBadge';

interface JobDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  job: Job | null;
}

export function JobDetailModal({ isOpen, onClose, job }: JobDetailModalProps) {
  const [showActivateService, setShowActivateService] = useState(false);
  // Activating a service mints a customer_services doc — an MSP surface. For a
  // tenant without the pack those rows would be invisible and unmanageable
  // (/home/services, the customer Services tab and /reports/services are all
  // gated), so the CTA itself is gated. docs/FEATURE_PACKS_PLAN.md.
  const canActivateService = useFeatureGate('service-activation');

  if (!isOpen || !job) return null;

  const showActivateCta =
    job.status === 'Completed' && !!job.customerId && canActivateService;

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString(getLocaleSync(), {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const getStatusColor = (status: string) => {
    const colors = {
      'Lead': 'bg-blue-500/20 text-blue-400',
      'Scoping': 'bg-purple-500/20 text-purple-400',
      'Quote Sent': 'bg-orange-500/20 text-orange-400',
      'Ready for Ops': 'bg-green-500/20 text-green-400',
      'Unscheduled': 'bg-yellow-500/20 text-yellow-400',
      'Scheduled': 'bg-blue-500/20 text-blue-400',
      'In Progress': 'bg-purple-500/20 text-purple-400',
      'Completed': 'bg-green-500/20 text-green-400',
      'Cancelled': 'bg-red-500/20 text-red-400',
      'Billing': 'bg-green-500/20 text-green-400',
      'won': 'bg-green-500/20 text-green-400',
    };
    return colors[status as keyof typeof colors] || 'bg-gray-500/20 text-gray-400';
  };

  const getPriorityColor = (priority?: string) => {
    if (!priority || priority === 'Normal') return '';
    const colors = {
      'Low': 'bg-blue-500/20 text-blue-400',
      'High': 'bg-orange-500/20 text-orange-400',
      'Critical': 'bg-red-500/20 text-red-400',
    };
    return colors[priority as keyof typeof colors] || '';
  };

  return (
    // z-[450] (was z-[250]) so the modal sits above CustomerDetailModal
    // even when the customer is provider-mounted at z-[400] (Customer 360
    // cross-entity flow). Page-level callers (SchedulePage, CustomersPage)
    // are unaffected — z-[450] is still above any page content.
    <div className="fixed inset-0 bg-black/70 z-[450] flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-gradient-to-br from-brand-primary to-brand-dark border border-brand-secondary/30 rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden shadow-2xl flex flex-col"
        onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-brand-secondary/20 to-transparent backdrop-blur-sm border-b border-white/10 p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-brand-secondary to-yellow-600 flex items-center justify-center shadow-lg">
              <Briefcase className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">{job.type}</h2>
              <p className="text-sm text-slate-400">
                <CustomerLink
                  customerId={job.customerId ?? null}
                  displayName={job.client}
                  className="text-slate-400 hover:text-brand-secondary"
                />
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="w-6 h-6 text-white" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Status & Priority */}
          <div className="flex items-center gap-3">
            <span className={`px-4 py-2 rounded-full text-sm font-semibold ${getStatusColor(job.status)}`}>
              {job.status}
            </span>
            {job.priority && job.priority !== 'Normal' && (
              <span className={`px-4 py-2 rounded-full text-sm font-semibold ${getPriorityColor(job.priority)}`}>
                {job.priority} Priority
              </span>
            )}
            {job.sos && (
              <span className="px-4 py-2 rounded-full text-sm font-semibold bg-red-500/20 text-red-400">
                🚨 SOS
              </span>
            )}
          </div>

          {/* Location & Schedule */}
          <div className="bg-white/5 rounded-lg border border-white/10 p-4 space-y-3">
            <h3 className="text-lg font-bold text-white mb-3">Details</h3>

            <div className="flex items-start gap-3 text-slate-300">
              <MapPin className="w-5 h-5 text-brand-secondary mt-0.5 flex-shrink-0" />
              <div className="min-w-0">
                <div className="font-semibold">{job.loc}</div>
                <div className="text-sm text-slate-400">{job.address}</div>
                {/* This job ran at a one-off address — say which, and what the
                    customer's usual address is, so the history reads straight. */}
                <SiteAddressNote job={job} tone="force-dark" className="mt-2" />
              </div>
            </div>

            {job.date && (
              <div className="flex items-center gap-3 text-slate-300">
                <Calendar className="w-5 h-5 text-brand-secondary" />
                <div>
                  <span className="font-semibold">Scheduled:</span> {job.date}
                  {job.time && <span> at {job.time}</span>}
                </div>
              </div>
            )}

            {job.duration && (
              <div className="flex items-center gap-3 text-slate-300">
                <Clock className="w-5 h-5 text-brand-secondary" />
                <div>
                  <span className="font-semibold">Duration:</span> {job.duration} minutes
                </div>
              </div>
            )}

            {job.contactName && (
              <div className="flex items-center gap-3 text-slate-300">
                <User className="w-5 h-5 text-brand-secondary" />
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="font-semibold">Contact:</span>
                  <span>{job.contactName}</span>
                  {job.contact && (
                    <>
                      <span>-</span>
                      <PhoneNumber phone={job.contact} />
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Services */}
          {job.services && job.services.length > 0 && (
            <div className="bg-white/5 rounded-lg border border-white/10 p-4">
              <h3 className="text-lg font-bold text-white mb-3">Services</h3>
              <div className="flex flex-wrap gap-2">
                {job.services.map((service, idx) => (
                  <span key={idx} className="px-3 py-1 bg-brand-secondary/20 text-brand-secondary rounded-full text-sm font-semibold">
                    {service}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          {job.notes && (
            <div className="bg-white/5 rounded-lg border border-white/10 p-4">
              <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                <FileText className="w-5 h-5 text-brand-secondary" />
                Notes
              </h3>
              <p className="text-slate-300 whitespace-pre-wrap">{job.notes}</p>
            </div>
          )}

          {/* Critical Notes */}
          {job.criticalNotes && (
            <div className="bg-red-500/10 rounded-lg border border-red-500/30 p-4">
              <h3 className="text-lg font-bold text-red-400 mb-3 flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                Critical Notes
              </h3>
              <p className="text-slate-300 whitespace-pre-wrap">{job.criticalNotes}</p>
            </div>
          )}

          {/* Timestamps */}
          <div className="bg-white/5 rounded-lg border border-white/10 p-4">
            <h3 className="text-lg font-bold text-white mb-3">Timeline</h3>
            <div className="space-y-2 text-sm text-slate-300">
              {job.createdAt && (
                <div>
                  <span className="text-slate-500">Created:</span> {formatDate(job.createdAt)}
                  {job.createdBy && <span className="text-slate-500"> by {job.createdBy}</span>}
                </div>
              )}
              {job.startedAt && (
                <div>
                  <span className="text-slate-500">Started:</span> {formatDate(job.startedAt)}
                </div>
              )}
              {job.completedAt && (
                <div>
                  <span className="text-slate-500">Completed:</span> {formatDate(job.completedAt)}
                </div>
              )}
              {job.actualDuration && (
                <div>
                  <span className="text-slate-500">Actual Duration:</span> {job.actualDuration} minutes
                </div>
              )}
            </div>
          </div>

          {/* Checklist */}
          {job.checklist && job.checklist.length > 0 && (
            <div className="bg-white/5 rounded-lg border border-white/10 p-4">
              <h3 className="text-lg font-bold text-white mb-3">Checklist</h3>
              <div className="space-y-2">
                {job.checklist.map((item, idx) => (
                  <div key={idx} className="flex items-start gap-2 text-slate-300">
                    <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${
                      item.completed ? 'bg-green-500/20 text-green-400' : 'bg-white/10 text-slate-500'
                    }`}>
                      {item.completed && '✓'}
                    </div>
                    <span className={item.completed ? 'line-through text-slate-500' : ''}>{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-white/10 p-4 bg-white/5">
          <div className="flex gap-3">
            {/* Show Activate Service button for completed jobs with customer */}
            {showActivateCta && (
              <button
                onClick={() => setShowActivateService(true)}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-brand-secondary to-yellow-600 hover:from-brand-secondary/90 hover:to-yellow-500 text-white font-bold rounded-lg transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
              >
                <Wifi className="w-4 h-4" />
                Activate Service
              </button>
            )}
            <button
              onClick={onClose}
              className={`px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-bold rounded-lg transition-colors ${
                showActivateCta ? 'flex-1' : 'w-full'
              }`}
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Activate Service Modal */}
      {canActivateService && (
        <ActivateServiceModal
          isOpen={showActivateService}
          onClose={() => setShowActivateService(false)}
          job={job}
        />
      )}
    </div>
  );
}
